import { Document } from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import { Fragment, type Node as ProseMirrorNode, type Schema, Slice } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { type Editor, EditorContent, type EditorOptions, Extension, type Extensions, useEditor } from '@tiptap/react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { forwardRef, type ReactNode, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Div } from '@ui/components/base/div';
import { Field, FieldLabel } from '@ui/components/base/field';
import { insertTemplateToken, redrawEditorSilently } from '@ui/components/editor/richTextEditorCommands';
import { buildEditableAttributes, PARAGRAPH_NODE_NAME } from '@ui/components/editor/richTextUtils';
import {
  TemplateTag,
  type TemplateTagProps,
  useTemplateTagConfiguration,
} from '@ui/components/editor/templateTagExtension';
import { createTemplateTagSuggestionBridge, TemplateTagSuggestion } from '@ui/components/editor/templateTagSuggestion';
import { TemplateTagSuggestionMenu } from '@ui/components/editor/TemplateTagSuggestionMenu';
import {
  flattenToSingleLine,
  replaceLineBreaksWithSpaces,
  SINGLE_LINE_SEPARATOR,
  toSingleLineTemplateDocument,
} from '@ui/components/editor/templateTagUtils';
import {
  TEMPLATE_INVALID_SURFACE_CLASS_NAME,
  TEMPLATE_PLACEHOLDER_CLASS_NAME,
} from '@ui/components/editor/templateTextUtils';
import { cn } from '@ui/lib/utils';

const SINGLE_LINE_PASTE_PLUGIN_KEY = new PluginKey('templateSingleLinePaste');
const OPEN_PARAGRAPH_DEPTH = 1;

const blockLineBreak = (): boolean => true;

const flattenSliceToSingleLine = (slice: Slice, schema: Schema): Slice => {
  const textblocks: ProseMirrorNode[] = [];
  slice.content.descendants((node) => {
    if (!node.isTextblock) {
      return true;
    }
    textblocks.push(node);
    return false;
  });
  if (textblocks.length <= 1) {
    return slice;
  }
  const inlineNodes = textblocks.reduce<ProseMirrorNode[]>((acc, textblock) => {
    if (textblock.childCount === 0) {
      return acc;
    }
    const separator = isEmpty(acc) ? [] : [schema.text(SINGLE_LINE_SEPARATOR)];
    return [...acc, ...separator, ...textblock.content.content];
  }, []);
  return new Slice(
    Fragment.from(schema.nodes.paragraph.create(null, inlineNodes)),
    OPEN_PARAGRAPH_DEPTH,
    OPEN_PARAGRAPH_DEPTH
  );
};

const SingleLineText = Extension.create({
  name: 'singleLineText',

  addKeyboardShortcuts() {
    return { Enter: blockLineBreak, 'Shift-Enter': blockLineBreak, 'Mod-Enter': blockLineBreak };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: SINGLE_LINE_PASTE_PLUGIN_KEY,
        props: {
          transformPastedText: (text) => flattenToSingleLine(text),
          transformPastedHTML: (html) => replaceLineBreaksWithSpaces(html),
          transformPasted: (slice) => flattenSliceToSingleLine(slice, this.editor.schema),
        },
      }),
    ];
  },
});

export const TEMPLATE_SINGLE_LINE_EXTENSIONS = [
  Document.extend({ content: PARAGRAPH_NODE_NAME }),
  UndoRedo,
  Paragraph,
  Text,
  TemplateTag,
  SingleLineText,
];

export interface TemplateTextFieldHandle {
  insertToken: (token: string) => void;
}

export interface TemplateTextFieldProps extends TemplateTagProps {
  value: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  placeholder?: string;
  invalid?: boolean;
  readOnly?: boolean;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  'aria-labelledby'?: string;
}

const readEditorText = (editor: Editor): string => (editor.isEmpty ? '' : editor.getText());

const TemplateTextField = forwardRef<TemplateTextFieldHandle, TemplateTextFieldProps>(
  (
    {
      value,
      onChange,
      label,
      placeholder,
      invalid = false,
      readOnly = false,
      className,
      onFocus,
      onBlur,
      tags,
      isTokenAvailable,
      unavailableTagTitle,
      'aria-labelledby': ariaLabelledBy,
    },
    ref
  ) => {
    const labelId = useId();
    const editable = !readOnly;
    const placeholderRef = useRef<string | undefined>(placeholder);
    const syncedValueRef = useRef<string>(value);
    const keepsSelectionRef = useRef<boolean>(false);
    const [initialContent] = useState(() => toSingleLineTemplateDocument(value));
    const [suggestionBridge] = useState(createTemplateTagSuggestionBridge);
    const [extensions] = useState<Extensions>(() => [
      ...TEMPLATE_SINGLE_LINE_EXTENSIONS,
      Placeholder.configure({ placeholder: () => placeholderRef.current ?? '' }),
      TemplateTagSuggestion.configure({ bridge: suggestionBridge }),
    ]);
    const labelledBy = ariaLabelledBy ?? (isNil(label) ? undefined : labelId);
    const editorProps = useMemo<EditorOptions['editorProps']>(
      () => ({
        attributes: buildEditableAttributes({
          className: readOnly
            ? cn(
                'flex min-w-0 flex-1 items-center px-2.5 outline-none',
                '[&_p]:m-0 [&_p]:wrap-break-word [&_p]:whitespace-pre-wrap',
                TEMPLATE_PLACEHOLDER_CLASS_NAME
              )
            : cn(
                'flex min-w-0 flex-1 items-center overflow-x-auto px-2.5 outline-none [scrollbar-width:none]',
                '[&_p]:m-0 [&_p]:whitespace-pre',
                TEMPLATE_PLACEHOLDER_CLASS_NAME
              ),
          multiline: false,
          ariaLabelledBy: labelledBy,
          invalid,
          readOnly,
        }),
      }),
      [labelledBy, invalid, readOnly]
    );
    const editor = useEditor({
      extensions,
      content: initialContent,
      immediatelyRender: false,
      shouldRerenderOnTransaction: false,
      editable,
      editorProps,
      onUpdate: ({ editor: updatedEditor }) => {
        const text = readEditorText(updatedEditor);
        syncedValueRef.current = text;
        onChange(text);
      },
      onFocus: () => {
        keepsSelectionRef.current = true;
        onFocus?.();
      },
      onBlur: () => {
        onBlur?.();
      },
    });
    useTemplateTagConfiguration(editor, { tags, isTokenAvailable, unavailableTagTitle });

    useImperativeHandle(ref, () => ({
      insertToken: (token: string) => {
        if (isNil(editor)) {
          return;
        }
        insertTemplateToken(editor, token, keepsSelectionRef.current);
      },
    }));

    useEffect(() => {
      if (isNil(editor) || editor.isFocused || syncedValueRef.current === value) {
        return;
      }
      if (readEditorText(editor) !== value) {
        editor.commands.setContent(toSingleLineTemplateDocument(value), { emitUpdate: false });
        keepsSelectionRef.current = false;
      }
      syncedValueRef.current = value;
    }, [editor, value]);

    useEffect(() => {
      if (isNil(editor) || editor.isEditable === editable) {
        return;
      }
      editor.setEditable(editable, false);
    }, [editor, editable]);

    useEffect(() => {
      placeholderRef.current = placeholder;
      if (isNil(editor) || editor.isDestroyed) {
        return;
      }
      redrawEditorSilently(editor);
    }, [editor, placeholder]);

    return (
      <Field data-slot="template-text-field" className={className}>
        {isNil(label) ? null : <FieldLabel id={labelId}>{label}</FieldLabel>}
        <Div
          data-invalid={invalid}
          data-read-only={readOnly}
          className={cn(
            'flex w-full min-w-0 items-stretch text-base md:text-sm',
            readOnly
              ? 'min-h-12'
              : cn(
                  'h-12 rounded-lg border border-input bg-background transition-colors',
                  'focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 dark:bg-input/30',
                  TEMPLATE_INVALID_SURFACE_CLASS_NAME
                )
          )}
        >
          {isNil(editor) ? null : <EditorContent editor={editor} className="flex min-w-0 flex-1" />}
        </Div>
        <TemplateTagSuggestionMenu bridge={suggestionBridge} />
      </Field>
    );
  }
);
TemplateTextField.displayName = 'TemplateTextField';

export { TemplateTextField };
